import * as React from 'react';
import _ = require('underscore');

import { InlineAlertType, InlineAlertWidget } from '@tableau/widgets';

import { Messages } from '~src/codegen/Localize';
import { interpolateString } from '~src/common/utilities/StringInterpolation';
import { ReactActionDialogBodyProps } from '~src/commonInfra/dialogs/ReactActionDialog';
import { showAddCredentialAuthModeDialog } from '~src/connectivity/components/AddCredentialAuthModeDialog';
import { OAuthCredentials } from '~src/connectivity/resources/OAuthCredentials';
import { AddOAuthCredentialAction } from '~src/connectivity/services/AddOAuthCredentialAction';
import { AddServiceAccountFileAction } from '~src/connectivity/services/AddServiceAccountFileCredentialAction';
import { EditableConnection } from '~src/connectivity/services/EditConnectionDialog';
import { FeatureFlags } from '~src/ts/FeatureFlags';
import { ListResult } from '~src/ts/fetchers/ListResult';
import { Filter } from '~src/ts/helpers/Filter';
import { SliceableHelper } from '~src/ts/helpers/SliceableHelper';
import { ResourceQuery } from '~src/ts/resources/ResourceQuery';
import { IOAuthCredential } from '~src/ts/ServerApi/types/IOAuthCredential';

import { EditOAuthConnectionDialogProps, EditOAuthConnectionDialogState } from '../../EditOAuthConnectionAction';
import { EditOAuthConnectionDialogEmbeddedToggle } from './EditOAuthConnectionDialogEmbeddedToggle';
import { EditOAuthConnectionDialogPrompt } from './EditOAuthConnectionDialogPrompt';
import { EditOAuthConnectionDialogTestConnection } from './EditOAuthConnectionDialogTestConnection';

export type EditOAuthConnectionDialogBodyProps = ReactActionDialogBodyProps<
    EditOAuthConnectionDialogProps,
    EditOAuthConnectionDialogState
>;

export function EditOAuthConnectionDialogBody(props: EditOAuthConnectionDialogBodyProps) {

    async function OnAddCredentialForSelectedAuthMode(props: any, state: any, updateDialogState: any, type: string){
        if (state.selectedConnectionAuthMode === Messages.addNewSavedCredential_OAuth()) {
            // if this is just a normal OAuth credential we are adding, then call
            // into attemptAuth to start the oauth flow.
            AddOAuthCredentialAction.attemptAuth(type).then(credentialId => {UpdateSelectedCredential(type, credentialId);});
          } else if (state.selectedConnectionAuthMode === Messages.addNewSavedCredential_ServiceAccount()) {
            // otherwise if this is a service account file credential we are adding,
            // try and parse the file and then store the new credential.
            let result = await AddServiceAccountFileAction
            .addServiceAccountFile(state.connAuthState.file, updateDialogState);
            UpdateSelectedCredential(type, result.id);
          }

    }

    async function UpdateSelectedCredential(firstConnectionType: string, credentialId: string) {
        refreshCredentials(firstConnectionType).then((credentialsListResult: ListResult<IOAuthCredential>) => {
            // Set the credentialId if it exists in the list
            if (_.find(credentialsListResult.items, (credential: IOAuthCredential) => credential.id === credentialId)) {
                props.updateDialogState({
                    credentialId: credentialId,
                });
            }
        });
    }

    const refreshCredentials = (
        firstConnectionType: string) => {
        const credentialsQuery: ResourceQuery<IOAuthCredential> =
            OAuthCredentials.all().addFilter(Filter.fieldEq('type', firstConnectionType));

        return SliceableHelper.fetchAll(credentialsQuery)
            .then((credentialsListResult: ListResult<IOAuthCredential>) => {
                props.updateDialogState({ credentials: credentialsListResult.items });
                return credentialsListResult; // For chaining
            });
    };

    const handleToggleChange = (isOAuthCredentialOption: boolean) => {
        if (isOAuthCredentialOption) {
            props.updateDialogState({ credentialOption: 'oauth' });
        } else {
            props.updateDialogState({ credentialOption: 'none' });
        }
    };

    const handleCredentialIdChange = (credentialId: string) => {
        if (credentialId === props.addNewOAuthCredentialOptionValue) {
            props.updateDialogState({
                credentialId: undefined,
            });
            const firstConnection: EditableConnection = props.firstConnection;
            const firstConnectionType = firstConnection.connectionType;
            const firstConnectionDetails = firstConnection.connectionDetails;
            let instanceUrl: string;
            let role: string;
            if (firstConnectionDetails.type === 'database') {
                instanceUrl = firstConnectionDetails.instanceUrl;
                role = firstConnectionDetails.role;
            }

            if (firstConnectionType === 'bigquery' && FeatureFlags.googleServiceAccount) {
                // TFS1243460 For GBQ, we now support Service Account file credentials AND OAuth, so the user must pick the mode.
                showAddCredentialAuthModeDialog(firstConnectionType, OnAddCredentialForSelectedAuthMode);
            }
            else{
                AddOAuthCredentialAction.attemptAuth(firstConnectionType, instanceUrl, role).then((credentialId: string) =>{
                    UpdateSelectedCredential(firstConnectionType, credentialId);
                });
            }
        } else {
            props.updateDialogState({ credentialId: credentialId });
        }
    };
    return (
        <>
            <EditOAuthConnectionDialogPrompt prompt={props.prompt} />
            <div style={{ width: '100%' }}>
                <EditOAuthConnectionDialogEmbeddedToggle
                    credentialNoValueOptionText={props.dialogState.credentialNoValueOptionText}
                    currentConnectionTypeDisplayName={props.currentConnectionTypeDisplayName}
                    addNewOAuthCredentialOptionValue={props.addNewOAuthCredentialOptionValue}
                    credentialId={props.dialogState.credentialId}
                    credentials={props.dialogState.credentials}
                    credentialOption={props.dialogState.credentialOption}
                    handleToggleChange={handleToggleChange}
                    handleCredentialIdChange={handleCredentialIdChange}
                />
                <EditOAuthConnectionDialogTestConnection
                    dialogState={props.dialogState}
                    onTestConnection={props.testConnection}
                    isTestConnectionDisabled={props.isTestConnectionDisabled(props.dialogState)}
                />
                {props.dialogState.usingManagedKeychain && <InlineAlertWidget
                    testId={'managed-keychain-alert'}
                    type={InlineAlertType.Warning}
                    children={interpolateString(Messages.managedKeychainEditConnectionWarningMessage(),{
                        LEARN_MORE: () => <>{<a href='https://help.tableau.com/current/server/en-us/protected_auth.htm'>{Messages.learnMore()}</a>}</>
                    })}/>}
            </div>
        </>
    );
}
